import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';
import { ResellerDashboardStats } from '../../types';

const ResellerDashboard: React.FC = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState<ResellerDashboardStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    setLoading(true);
    const response = await api.getResellerDashboard();
    if (response.success && response.data) {
      setStats(response.data);
    } else {
      setError(response.error || 'Failed to load dashboard');
    }
    setLoading(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="flex flex-col items-center gap-4">
          <div className="w-12 h-12 border-4 border-emerald-500/30 border-t-emerald-500 rounded-full animate-spin" />
          <p className="text-neutral-500 text-sm">Loading dashboard...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-500/10 border border-red-500/30 rounded-2xl p-6 text-center">
        <p className="text-red-400">{error}</p>
        <button onClick={loadDashboard} className="mt-4 px-4 py-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-white">Welcome, {user?.firstName || 'Reseller'}!</h1>
          <p className="text-neutral-500">Your reseller dashboard overview</p>
        </div>
        <Link
          to="/reseller/create-client"
          className="px-6 py-3 bg-emerald-600 hover:bg-emerald-500 text-white rounded-xl transition-colors font-medium flex items-center gap-2"
        >
          <span>+</span>
          Add New Client
        </Link>
      </div>

      {/* Stats Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Clients */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">👥</span>
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase rounded">
              Total
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.totalClients || 0}</p>
          <p className="text-neutral-500 text-sm mt-1">
            {stats?.activeClients || 0} active • {stats?.pendingClients || 0} pending
          </p>
        </div>

        {/* Active Clients */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">✅</span>
            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 text-[10px] font-bold uppercase rounded">
              Active
            </span>
          </div>
          <p className="text-3xl font-bold text-white">{stats?.activeClients || 0}</p>
          <p className="text-neutral-500 text-sm mt-1">
            {stats?.totalClients ? ((stats.activeClients / stats.totalClients) * 100).toFixed(0) : 0}% of total
          </p>
        </div>

        {/* Total Earnings */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">💰</span>
            <span className="px-2 py-1 bg-emerald-500/20 text-emerald-400 text-[10px] font-bold uppercase rounded">
              Earned
            </span>
          </div>
          <p className="text-3xl font-bold text-white">৳{(stats?.totalEarnings || 0).toLocaleString()}</p>
          <p className="text-neutral-500 text-sm mt-1">Total commission earned</p>
        </div>

        {/* Pending Payout */}
        <div className="bg-[#121212] border border-[#262626] rounded-2xl p-6">
          <div className="flex items-center justify-between mb-4">
            <span className="text-2xl">⏳</span>
            <span className={`px-2 py-1 ${(stats?.pendingPayout || 0) > 0 ? 'bg-yellow-500/20 text-yellow-400' : 'bg-neutral-500/20 text-neutral-400'} text-[10px] font-bold uppercase rounded`}>
              Pending
            </span>
          </div>
          <p className="text-3xl font-bold text-white">৳{(stats?.pendingPayout || 0).toLocaleString()}</p>
          <Link to="/reseller/commissions" className="text-emerald-500 text-sm hover:text-emerald-400 transition-colors">
            View commissions →
          </Link>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Recent Clients */}
        <div className="lg:col-span-2 bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Recent Clients</h3>
            <Link to="/reseller/clients" className="text-emerald-500 text-xs hover:text-emerald-400 transition-colors">
              View All →
            </Link>
          </div>
          {stats?.recentClients && stats.recentClients.length > 0 ? (
            <div className="divide-y divide-[#262626]">
              {stats.recentClients.map((client) => (
                <div key={client.id} className="p-4 hover:bg-[#1a1a1a] transition-colors">
                  <div className="flex items-center gap-4">
                    <div className="w-10 h-10 bg-emerald-500/20 rounded-full flex items-center justify-center">
                      <span className="text-emerald-400 font-bold">
                        {client.companyName?.charAt(0) || client.email.charAt(0).toUpperCase()}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="font-medium text-white truncate">{client.companyName || 'No Company'}</p>
                      <p className="text-neutral-500 text-sm truncate">{client.email}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-white font-medium">{client.planName}</p>
                      <span className={`text-xs px-2 py-0.5 rounded ${
                        client.status === 'active'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : client.status === 'pending'
                            ? 'bg-yellow-500/20 text-yellow-400'
                            : 'bg-red-500/20 text-red-400'
                      }`}>
                        {client.status}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-[#1a1a1a] rounded-full flex items-center justify-center mx-auto mb-4">
                <span className="text-3xl">👥</span>
              </div>
              <p className="text-neutral-500">No clients yet</p>
              <Link to="/reseller/create-client" className="text-emerald-500 text-sm hover:text-emerald-400">
                Add your first client →
              </Link>
            </div>
          )}
        </div>

        {/* Quick Actions & This Month */}
        <div className="space-y-6">
          {/* This Month Earnings */}
          <div className="bg-gradient-to-r from-emerald-500/10 to-blue-500/10 border border-emerald-500/20 rounded-2xl p-6">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
                <span className="text-2xl">📈</span>
              </div>
              <div>
                <p className="text-neutral-400 text-sm">This Month</p>
                <p className="text-2xl font-bold text-white">৳{(stats?.thisMonthEarnings || 0).toLocaleString()}</p>
              </div>
            </div>
          </div>

          {/* Quick Actions */}
          <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
            <div className="p-4 border-b border-[#262626]">
              <h3 className="text-sm font-bold text-white uppercase tracking-wider">Quick Actions</h3>
            </div>
            <div className="p-4 space-y-3">
              <Link
                to="/reseller/create-client"
                className="flex items-center gap-3 p-3 bg-emerald-500/10 border border-emerald-500/20 rounded-xl hover:bg-emerald-500/20 transition-colors group"
              >
                <span className="text-xl">➕</span>
                <div className="flex-1">
                  <p className="text-white font-medium group-hover:text-emerald-400 transition-colors">Add Client</p>
                  <p className="text-neutral-500 text-xs">Register new client</p>
                </div>
                <span className="text-neutral-500 group-hover:text-emerald-400 transition-colors">→</span>
              </Link>

              <Link
                to="/reseller/clients"
                className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
              >
                <span className="text-xl">👥</span>
                <div className="flex-1">
                  <p className="text-white font-medium">My Clients</p>
                  <p className="text-neutral-500 text-xs">Manage clients</p>
                </div>
                <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
              </Link>

              <Link
                to="/reseller/commissions"
                className="flex items-center gap-3 p-3 bg-[#1a1a1a] border border-[#262626] rounded-xl hover:bg-[#262626] transition-colors group"
              >
                <span className="text-xl">💰</span>
                <div className="flex-1">
                  <p className="text-white font-medium">Commissions</p>
                  <p className="text-neutral-500 text-xs">View earnings</p>
                </div>
                <span className="text-neutral-500 group-hover:text-white transition-colors">→</span>
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Commissions */}
      {stats?.recentCommissions && stats.recentCommissions.length > 0 && (
        <div className="bg-[#121212] border border-[#262626] rounded-2xl overflow-hidden">
          <div className="p-4 border-b border-[#262626] flex items-center justify-between">
            <h3 className="text-sm font-bold text-white uppercase tracking-wider">Recent Commissions</h3>
            <Link to="/reseller/commissions" className="text-emerald-500 text-xs hover:text-emerald-400 transition-colors">
              View All →
            </Link>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-[#1a1a1a]">
                <tr>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Client</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Amount</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Commission</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Status</th>
                  <th className="text-left text-xs font-medium text-neutral-500 uppercase p-4">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#262626]">
                {stats.recentCommissions.slice(0, 5).map((commission) => (
                  <tr key={commission.id} className="hover:bg-[#1a1a1a] transition-colors">
                    <td className="p-4">
                      <p className="text-white font-medium">{commission.clientId.substring(0, 8)}...</p>
                    </td>
                    <td className="p-4">
                      <p className="text-neutral-400">৳{commission.subscriptionAmount.toLocaleString()}</p>
                    </td>
                    <td className="p-4">
                      <p className="text-emerald-400 font-medium">৳{commission.commissionAmount.toLocaleString()}</p>
                    </td>
                    <td className="p-4">
                      <span className={`px-2 py-1 rounded-lg text-xs font-medium ${
                        commission.status === 'paid'
                          ? 'bg-emerald-500/20 text-emerald-400'
                          : commission.status === 'approved'
                            ? 'bg-blue-500/20 text-blue-400'
                            : 'bg-yellow-500/20 text-yellow-400'
                      }`}>
                        {commission.status}
                      </span>
                    </td>
                    <td className="p-4">
                      <p className="text-neutral-500 text-sm">
                        {new Date(commission.createdAt).toLocaleDateString('en-BD')}
                      </p>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default ResellerDashboard;
